import { useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nosible/shared/components/ui/table";
import { Button } from "@nosible/shared/components/ui/button";
import { Badge } from "@nosible/shared/components/ui/badge";
import { useSession } from "@/admin/lib/auth-client";
import { trpc } from "@/admin/lib/trpc";
import { Building2, ArrowLeft, Users, CreditCard, Key, ChevronRight, ExternalLink, Plus } from "lucide-react";
import { AdminEditMemberRole } from "@/admin/components/AdminEditMemberRole";
import { AdminInviteMember } from "@/admin/components/AdminInviteMember";

export function AdminOrganizationPage() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { data: session, isPending: sessionPending } = useSession();

  const {
    data,
    isLoading,
    error,
  } = trpc.admin.orgBySlug.useQuery(
    { slug: slug! },
    { enabled: !!session && !!slug }
  );

  const organization = data?.organization;
  const members = data?.members;
  const subscriptions = data?.subscriptions;

  useEffect(() => {
    if (!session && !sessionPending) {
      navigate("/login");
    }
  }, [session, sessionPending, navigate]);

  if (sessionPending || isLoading || !data) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-silica">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <div className="text-red-400">{error.message}</div>
        <Button variant="outline" onClick={() => navigate("/organizations")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Organizations
        </Button>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <div className="text-chromium-mist">Organization not found</div>
        <Button variant="outline" onClick={() => navigate("/organizations")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Organizations
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <Button variant="ghost" size="sm" asChild className="mb-4">
          <Link to="/organizations">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Organizations
          </Link>
        </Button>
        <div className="flex items-center gap-3">
          {organization.logo ? (
            <img
              src={organization.logo}
              alt={organization.name}
              className="h-12 w-12 rounded-lg object-cover"
            />
          ) : (
            <div className="h-12 w-12 rounded-lg bg-slate-core flex items-center justify-center">
              <Building2 className="h-6 w-6 text-chromium-mist" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-silica font-display">{organization.name}</h1>
            <p className="text-chromium-mist font-mono text-sm">{organization.slug}</p>
          </div>
          <Badge className="ml-auto" variant="outline">Admin View</Badge>
        </div>
      </div>

      <div className="grid gap-6">
        {/* Organization Info */}
        <Card>
          <CardHeader>
            <CardTitle>Organization Details</CardTitle>
            <CardDescription>Basic information about this organization</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-chromium-mist">Organization ID</p>
                <p className="text-silica font-mono text-xs">{organization.id}</p>
              </div>
              <div>
                <p className="text-sm text-chromium-mist">Slug</p>
                <p className="text-silica font-mono text-xs">{organization.slug}</p>
              </div>
              <div>
                <p className="text-sm text-chromium-mist">Members</p>
                <p className="text-silica font-medium">{organization.memberCount}</p>
              </div>
              <div>
                <p className="text-sm text-chromium-mist">Created</p>
                <p className="text-silica font-medium">
                  {organization.createdAt
                    ? new Date(organization.createdAt).toLocaleDateString()
                    : "—"}
                </p>
              </div>
              {organization.url && (
                <div className="col-span-2">
                  <p className="text-sm text-chromium-mist">Website</p>
                  <a
                    href={organization.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-silica font-mono text-xs hover:text-neon-green flex items-center gap-1"
                  >
                    {organization.url}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Subscriptions */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Subscriptions
                </CardTitle>
                <CardDescription>Organization subscriptions and API access</CardDescription>
              </div>
              <Button asChild size="sm" className="bg-neon-green text-obsidian hover:bg-neon-green/90">
                <Link to={`/subscriptions/create?ownerId=${organization.id}&ownerType=organization`}>
                  <Plus className="h-4 w-4 mr-1" />
                  Create Subscription
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {!subscriptions || subscriptions.length === 0 ? (
              <div className="text-center py-4">
                <CreditCard className="h-8 w-8 mx-auto mb-2 text-chromium-mist opacity-50" />
                <p className="text-chromium-mist">No active subscriptions</p>
              </div>
            ) : (
              <div className="space-y-3">
                {subscriptions.map((sub) => (
                  <div
                    key={sub.id}
                    className="flex items-center justify-between p-3 bg-slate-core/30 rounded-lg border border-slate-core"
                  >
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="text-silica font-medium capitalize">
                          {sub.plan?.name ?? "Unknown plan"}
                        </p>
                        <p className="text-xs text-chromium-mist capitalize">{sub.status}</p>
                        <p className="text-xs text-chromium-mist font-mono">{sub.id}</p>
                      </div>
                    </div>
                    <Button asChild variant="outline" size="sm">
                      <Link to={`/subscriptions/${sub.id}`}>
                        <Key className="h-4 w-4 mr-2" />
                        Manage
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Link>
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Members */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Members
                </CardTitle>
                <CardDescription>
                  People who have access to this organization
                </CardDescription>
              </div>
              <AdminInviteMember
                organizationId={organization.id}
                organizationName={organization.name}
              />
            </div>
          </CardHeader>
          <CardContent>
            {members?.length === 0 ? (
              <div className="text-center py-4 text-chromium-mist">
                <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No members found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members?.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell className="font-medium text-silica">
                        {member.userName || "—"}
                      </TableCell>
                      <TableCell className="text-chromium-mist">
                        {member.userEmail}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">{member.role}</Badge>
                      </TableCell>
                      <TableCell className="text-chromium-mist">
                        {member.createdAt
                          ? new Date(member.createdAt).toLocaleDateString()
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <AdminEditMemberRole
                            memberId={member.id}
                            currentRole={member.role}
                            memberName={member.userName}
                          />
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={`/users/${member.userId}`}>
                              View User
                            </Link>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
